import { UserOccupation } from '@/types/user'
import { CreditShowcaseProductKey, creditProducts } from './credit-products'

export type CreditProfileKind = 'student' | 'doctor' | 'professional'

const productsByProfile: Record<CreditProfileKind, CreditShowcaseProductKey[]> =
  {
    student: ['allowance', 'home_equity'],
    doctor: ['personal_loan', 'home_equity', 'rcp'],
    professional: ['home_equity'],
  }

const profileLabel: Record<CreditProfileKind, string> = {
  student: 'Estudante',
  doctor: 'Médico',
  professional: 'Profissional',
}

const months = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
]

export function profileOf(
  occupation: UserOccupation | null | undefined,
): CreditProfileKind {
  if (occupation === 'student') return 'student'
  if (occupation === 'doctor' || occupation === 'resident') return 'doctor'
  return 'professional'
}

export function profileName(profile: CreditProfileKind) {
  return profileLabel[profile]
}

export function productsFor(profile: CreditProfileKind) {
  return productsByProfile[profile]
}

/**
 * Data em que o produto libera, pela data de formatura.
 * O curso tem 12 semestres e a formatura cai no fim do 12º,
 * então faltam (13 - semestre mínimo) semestres até ela.
 */
export function unlockDate(graduationDate: string, minSemester: number): Date {
  const [year, month] = graduationDate.split('-').map(Number)
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, 1))
  date.setUTCMonth(date.getUTCMonth() - (13 - minSemester) * 6)
  return date
}

export function formatMonthYear(date: Date) {
  return `${months[date.getUTCMonth()]}/${date.getUTCFullYear()}`
}

export type ProductLock = {
  message: string
}

export function lockOf(params: {
  product: CreditShowcaseProductKey
  profile: CreditProfileKind
  currentSemester: number | null
  graduationDate: string | null
}): ProductLock | null {
  const { product, profile, currentSemester, graduationDate } = params
  const minSemester = creditProducts[product].minSemester

  if (profile !== 'student' || !minSemester) return null
  if (currentSemester == null || currentSemester >= minSemester) return null

  const current = Math.max(currentSemester, 1)
  const release = graduationDate
    ? `, libera em ${formatMonthYear(unlockDate(graduationDate, minSemester))}`
    : ''

  return {
    message: `Disponível a partir do ${minSemester}º semestre. Você está no ${current}º${release}.`,
  }
}

export function chipText(params: {
  profile: CreditProfileKind
  currentSemester: number | null
  crm: string | null
  crmUf: string | null
}) {
  const { profile, currentSemester, crm, crmUf } = params
  const label = profileLabel[profile]

  if (profile === 'student') {
    if (currentSemester === 0) return `${label} · formado?`
    if (currentSemester != null && currentSemester > 0) {
      return `${label} · ${currentSemester}º semestre`
    }
    return label
  }

  if (profile === 'doctor' && crm && crmUf)
    return `${label} · CRM-${crmUf} ${crm}`

  return label
}
